import useScopedLoad from './useScopedLoad';
// src/hooks/useOpenSettlements.js
// © 2025 COPYright LAHO – https://www.linkedin.com/in/laurens-holtkamp-7a686b2b/

import { useCallback, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { fetchGroupTransactions } from '../services/dataService';
import { useGroup } from '../context/GroupContext';
import { useUser } from '../context/AuthContext';
import { BEER_PRICE, CUP_REFUND } from '../config';
import { calcSummary } from '../utils/calcSummary';

export const hasPaidRequest = (paidRequests, { payerId, receiverId, matchId, amountCents }) =>
  paidRequests.some(
    (paymentRequest) =>
      paymentRequest.payer_id === payerId &&
      paymentRequest.receiver_id === receiverId &&
      (paymentRequest.match_id || null) === (matchId || null) &&
      Number(paymentRequest.amount_cents) === amountCents
  );

/**
 * Openstaande verrekeningen voor de huidige gebruiker:
 *
 * - openCount: aantal kaarten (user + match) waar JIJ de kaarthouder bent
 *   én waar nog een positief totaal openstaat
 *   én waar nog GEEN settlement voor bestaat.
 *
 * - openItems: lijst met:
 *   {
 *     group_id,
 *     owner_id,      // altijd current user
 *     card_user_id,  // altijd current user (kaart van jou)
 *     match_id,      // kan null zijn
 *     amount,        // totaal te ontvangen bedrag > 0
 *     summary        // calcSummary-resultaat voor deze kaart in deze match
 *   }
 */
const useOpenSettlements = () => {
  const { group } = useGroup();
  const { user } = useUser();

  const load = useCallback(async () => {
    const [tx, st, requests] = await Promise.all([
      fetchGroupTransactions(group.id),
      supabase.from('settlements').select('*').eq('group_id', group.id),
      supabase.from('payment_requests').select('payer_id,receiver_id,match_id,amount_cents')
        .eq('group_id', group.id).eq('payer_id', user.id).eq('status', 'paid'),
    ]);
    for (const result of [st, requests]) if (result.error) throw result.error;
    return { transactions: tx || [], settlements: st.data || [], paidRequests: requests.data || [] };
  }, [group?.id, user?.id]);
  const { data, loading, error, hasLoaded, refresh: fetchAll } = useScopedLoad(
    group?.id && user?.id ? `${group.id}:${user.id}` : null, load);
  const { transactions = [], settlements = [], paidRequests = [] } = data || {};

  // ook luisteren naar globale events (bij togglen in TransactionSummary)
  useEffect(() => {
    const handler = () => fetchAll();
    window.addEventListener('bb:settlements-changed', handler);
    return () => window.removeEventListener('bb:settlements-changed', handler);
  }, [fetchAll]);

  const { openItems, payableItems, openCount, payableCount } = useMemo(() => {
    if (!group?.id || !user?.id || !transactions.length) {
      return { openItems: [], payableItems: [], openCount: 0, payableCount: 0 };
    }

    // Groepeer transacties per match (null = zonder specifieke match)
    const byMatch = new Map();
    for (const tx of transactions) {
      const key = tx.match_id || null;
      if (!byMatch.has(key)) byMatch.set(key, []);
      byMatch.get(key).push(tx);
    }

    const open = [];
    const payable = [];

    for (const [matchKey, txs] of byMatch.entries()) {
      if (!txs || !txs.length) continue;

      // Zelfde logica als in TransactionSummary, maar dan per match-groep
      const summaryForMatch = calcSummary(txs, { BEER_PRICE, CUP_REFUND });

      Object.entries(summaryForMatch).forEach(([cardUserId, item]) => {
        const tegen = item.tegen || {};
        const hasSettlement = settlements.some(
          (settlement) =>
            settlement.group_id === group.id &&
            settlement.owner_id === cardUserId &&
            settlement.card_user_id === cardUserId &&
            (settlement.match_id || null) === (matchKey || null)
        );
        if (hasSettlement) return;

        if (cardUserId === user.id) {
          const positiveTotal = Object.values(tegen)
            .filter((v) => v > 0)
            .reduce((a, b) => a + b, 0);
          if (positiveTotal > 0) {
            open.push({ group_id: group.id, owner_id: user.id, card_user_id: cardUserId, match_id: matchKey, amount: positiveTotal, summary: item });
          }
          return;
        }

        const amountToPay = Number(tegen[user.id]) || 0;
        if (amountToPay > 0) {
          const amountCents = Math.round(amountToPay * 100);
          const isAlreadyPaid = hasPaidRequest(paidRequests, {
            payerId: user.id,
            receiverId: cardUserId,
            matchId: matchKey,
            amountCents,
          });
          if (isAlreadyPaid) return;
          payable.push({ group_id: group.id, owner_id: cardUserId, card_user_id: cardUserId, payer_id: user.id, match_id: matchKey, amount: amountToPay, summary: item });
        }
      });
    }

    return { openItems: open, payableItems: payable, openCount: open.length, payableCount: payable.length };
  }, [transactions, settlements, paidRequests, group, user]);

  return {
    transactions,
    openItems,
    openCount,
    payableItems,
    payableCount,
    loading,
    hasLoaded,
    error,
    refresh: fetchAll,
  };
};

export default useOpenSettlements;
